import { Injector, Injectable } from '@angular/core';
import { BaseWrapperService } from '../base-service/base-wrapper.service';
import * as moment from 'moment';

declare var webserviceUrl: String;

const EARLY_DATE = '1800-01-01';

@Injectable()
export class ControlValidationMessagesService {

    static getCustomErrorMessage(code: string, controlName: string) {
        // Separate Control Name by Capital Letters to get individual words and capitalize first letter of first word
        let splitControlName = controlName.split(/(?=[A-Z])/);
        splitControlName[0] = splitControlName[0][0].toUpperCase() + splitControlName[0].slice(1);

        // Separate split name by space to get custom validation error message for control.
        const config = {
            'required': `${splitControlName.join(' ').toString()} is required`
        };

        return config[code];
    }

    static getValidatorErrorMessage(code: string) {
        const config = {
            'maxlength': 'Value exceeds maximum length',
            'max': 'Value exceeds maximum value',
            'required': 'Required',
            'requiredInteger': 'An Integer is required',
            'Integer': 'Must be an integer',
            'positiveNumberOrNull': 'Must be empty or a non negative number',
            'nonNegativeNumber': 'Must be zero or greater',
            'positiveNumber': 'Must be greater than zero',
            'positiveNumberRequired': 'A number greater than zero is required',
            'numberToLarge': 'A number less than 2147483647 is required',
            'nonZeroNumber': 'Must be non-zero',
            'requireSelectedItem': 'Selection required',
            'requiredModelYear': 'Model Year must be equal or greater than 1800 and less than ' + (new Date().getFullYear() + 3),
            'maxAircraftModelYear': 'Model Year must be less than ' + (new Date().getFullYear() + 2),
            'requiredVINPattern': 'Incorrect VIN format',
            'requiredVINPattern2': 'Incorrect VIN format 2',
            'yearFirstRegisteredRange': `Year first registered must be between 1800 and ${new Date().getFullYear()}`,
            'yearFirstRegisteredRange2': `Year first registered must be between 1800 and ${(new Date().getFullYear() + 1)}`,
            'invalidCreditCard': 'Is invalid credit card number',
            'invalidEmailAddress': 'Invalid email address',
            'invalidPassword': 'Invalid password. Password must be at least 6 characters long, and contain a number.',
            'invalidShortDate': 'Invalid date',
            'invalidFormat': 'Invalid format',
            'sequenceCountGreaterThan250': 'You can only receive 250 items at a time.',
            'inventoryLowPriority': 'Must be greater than medium priority',
            'inventoryMediumPriority': 'Must be greater than high priority',
            'positiveIntegerRequired': 'A positive integer is required',
            'nonNegativeIntegerRequired': 'A non-negative integer is required',
            'positiveNumeric': 'Must be a positive numeric number.',
            'percentageMaxNotRequired': 'Percentage must be greater than 0 less than 100',
            'percentageMaxRequired': 'Percentage must be greater than 0 less than 100',
            'invalidGLAccountFormat': 'The GL Account number must be in the format XXXX-XX-XXX-XX-XX',
            'glRequired': 'GL Required',
            'only1Residential': 'Only 1 Residential address is allowed',
            'only1Mailing': 'Only 1 Mailing address is allowed',
            'only1Commercial': 'Only 1 Commerical address is allowed',
            'addressLine1MaxLength': 'Street Address has a maximum length of 40 characters',
            'addressLine2MaxLength': 'Apt, Ste, Etc. has a maximum length of 40 characters',
            'zipcodeLength': 'ZIP Code has a maximum length of 10 characters',
            'cityLength': 'City has a maximum length of 20 characters',
            'countyNotAllowed': 'County is only allowed when State is AZ',
            'countyRequired': 'County is required when State is AZ',
            'only1Type': 'Type must be unique',
            'endDateGreaterThanEffectiveDate': 'The expiration date must be greater than or equal to effective date',
            'dateCannotBeInFuture': 'Date cannot be in the future',
            'dateCannotBeInPast': 'Expiration date cannot be in the past',
            'sevisNotValid': 'Invalid format: 10 numbers are required',
            'ssnNotValid': 'Invalid Format: 9 numbers are requred',
            'i94NotValid': 'Invalid format: 11 alphanumeric characters are required',
            'passportNumberNotValid': 'passport should be between 6-12 digits',
            'alienNumberNotValid': 'Invalid format: 9 numbers are required',
            'visaNumberNotValid': 'Invalid format: 8 alphanumeric characters are required',
            'vlsDocumentDescriptionNotValid': 'Invalid format: 35 alphanumeric characters are required, \' is not allowed ',
            'uscisCaseNumberNotValid': 'Invalid format: 15 alphanumeric characters are required',
            'racfIdLength': 'RACF ID must be between 5 to 15 characters',
            'ppvMainframeBatchLength': 'Mainframe Batch must be between 4 to 5 characters',
            'invalidInteger': 'An invalid integer was entered',
            'examDateValidator': 'The Exam is more than 90 days old. You must submit a Exam that is less than 90 days old.',
            'permanentResidentReceiptNumber': 'Invalid format: First 3 characters must be letters and remaining characters must be numeric.',
            'employmenteAuthorizationCardNumber': 'Invalid format: First 3 characters must be letters and remaining characters must be numeric.',
            'naturalizationCertificateNumber': 'Invalid format: 6-12 characters are required. Use leading 0\'s if there is less than 6 characters are provided.',
            'citizenshipCertificateNumber': 'Invalid format: 6-12 characters are required. Use leading 0\'s if there is less than 6 characters are provided.',
            'vinOrTitleNumber': 'VIN and/or Title are required',
            'invalidFirstName': 'Invalid format: First name does not allow any special characters',
            'invalidName': 'Invalid format: Name does not allow any special characters',
            'nameValidator': 'Invalid format: alpha characters are required',
            'documentCertificateNumber': 'First 3 characters must be capital letters and remaining characters must be numeric',
            'invalidMonth': 'Month value must be between 1 and 12',
            'invalidMinimumYear': 'Year value must be greater than 1752',
            'invalidMinimumDate': 'Date too early',
            'minDateRequired': 'Date required',
            'documentDescriptionLength': 'The length of the Document Description parameter is invalid. Document Descriptions can be up to 35 characters long.',
            'maxCustomerAccountCheckout': 'Can not exceed Customer Account Balance',
            'maxCustomerAccountTransactionAmount': 'Can not exceed Total Transaction Amount',
            'invalidUserName': 'A username cannot contain any special characters or spaces',
            'allowPositiveNumberAndDecimals': 'Allow only positive number and decimals',
            'maxBondAmountExceeded': 'The bond amount can not exceed 999,999.99',
            'nonNegativeNumberIncludeComma': 'Must be zero or greater',
            'minAndMaxNumber': 'Number must be greater than 0.0001 and less than 10.0000',
            'highCategoryLimit': 'The "High" percentage must be 25 or higher',
            'lowCategoryLimit': 'The "Low" percentage must be 15 or higher',
            'invalidFactoryListPrice': 'Price must be between $.01 and $999,999.99',
            'postalCodeRequired': 'Postal zip code is required',
            'inavlidPostalCode': 'Invalid postal zip code',
            'fourDigitYearRequired': 'A four digit year is required',
            'invalidPhone': 'Invalid phone number',
            'invalidSendTo': 'Invalid send to',
            'mustBe10Digits': 'Must be 10 digits',
            'negativeNumberRequired': 'A negative number is required'
        };
        return config[code];
    }

    static glAccountFormat(control) {
        if (control && control.value) {
            if (control.value.match(/^[\d]{4}-[\d]{2}-[\d]{3}-[\d]{2}-[\d]{2}$/)) {
                return null;
            } else {
                return { 'invalidGLAccountFormat': true };
            }
        }
        return null;
    }

    static positiveNumberOrNull(control) {
        if (control.value === null) {
            return null;
        }

        if (control.value === '') {
            return null;
        }

        if (control.value >= 0) {
            return null;
        }

        return { 'positiveNumberOrNull': true };
    }

    static racfIdLength(control) {
        if (control.value != null && (control.value.length >= 5 && control.value.length <= 15)) {
            return null;
        }

        return { 'racfIdLength': true };
    }

    static ppvMainframeBatchLength(control) {
        if (control.value != null && (control.value.length >= 4 && control.value.length <= 5)) {
            return null;
        }

        return { 'ppvMainframeBatchLength': true };
    }

    static validateInteger(control) {
        if (control && control.value) {
            if (!isNaN(control.value) && control.value.match(/^[0-9]{1,10}$/)) {
                var integer = parseInt(control.value);
                if (integer <= 2147483647 && -2147483648 <= integer) {
                    return null;
                } else {
                    return { 'invalidInteger': true };
                }
            } else {
                return { 'invalidInteger': true };
            }
        }
        return null;
    }

    static percentageMaxNotRequired(control) {
        if (!control.value || ((control.value - 0) == control.value) && (control.value > 0 && control.value <= 100)) {
            return null;
        }

        return { 'percentageMaxNotRequired': true };
    }

    static percentageMaxRequired(control) {
        if (control.value || ((control.value - 0) == control.value) && (control.value > 0 && control.value <= 100)) {
            return null;
        }

        return { 'percentageMaxRequired': true };
    }

    static nonNegativeNumber(control) {
        if (((control.value - 0) == control.value) && (('' + control.value).trim().length > 0) && (control.value >= 0)) {
            return null;
        }

        return { 'nonNegativeNumber': true };
    }

    static nonNegativeNumberIncludeComma(control) {
        control = parseFloat(control.value.replace(/,/g, ''));
        if (((control - 0) == control) && (('' + control).trim().length > 0) && (control >= 0)) {
            return null;
        }

        return { 'nonNegativeNumber': true };
    }

    static positiveNumberRequred(control) {
        if (((control.value - 0) == control.value) && (('' + control.value).trim().length > 0) && (control.value > 0)) {
            return null;
        }

        return { 'positiveNumberRequired': true };
    }

    static negativeNumberRequired(control) {
        if (((control.value - 0) == control.value) && (('' + control.value).trim().length > 0) && (control.value < 0)) {
            return null;
        }

        return { 'negativeNumberRequired': true };
    }

    static maxBondAmount(control) {
        if (control.value && control.value && control.value > 999999.99) {
            return { 'maxBondAmountExceeded': true };
        }
    }

    static positiveNumber(control) {
        if (control.value && control.value && control.value > 2147483647) {
            return { 'numberToLarge': true };
        }

        if (!control.value || ((control.value - 0) == control.value) && (control.value > 0)) {
            return null;
        }

        return { 'positiveNumber': true };
    }

    static nonZeroNumber(control) {
        if (((control.value - 0) == control.value) && (('' + control.value).trim().length > 0) && (control.value != 0)) {
            return null;
        }

        return { 'nonZeroNumber': true };
    }

    static nonZeroPositiveNumber(control) {
        if (((control.value - 0) == control.value) && (('' + control.value).trim().length > 0) && (control.value != 0)) {
            return null;
        }

        return { 'nonZeroNumber': true };
    }

    static positiveIntegerRequired(control) {
        var regInteger = new RegExp('^-?[0-9]+$');

        var reg = new RegExp('(^([0-9]*[.][0-9]*[0-9]+[0-9]*)$)|(^([0-9]*[0-9]+[0-9]*[.][0-9]+)$)|(^([0-9]+[0-9]*)$)');

        if (reg.test(control.value) && regInteger.test(control.value)) {
            if (+control.value <= 0) {
                return { 'positiveIntegerRequired': true };
            } else if (+control.value > 2147483647) {
                return { 'numberToLarge': true };
            } else {
                return null;
            }

        }

        return { 'positiveIntegerRequired': true };
    }

    static nonNegativeIntegerRequired(control) {
        // Test for regular integer. Check for < 0 below.
        var reg = new RegExp('^-?[0-9]+$');

        if (reg.test(control.value)) {
            if (+control.value < 0) {
                return { 'nonNegativeIntegerRequired': true };
            } else if (+control.value > 2147483647) {
                return { 'numberToLarge': true };
            } else {
                return null;
            }

        }

        return { 'nonNegativeIntegerRequired': true };
    }

    static positiveNumeric(control) {
        var regInteger = new RegExp('^-?[0-9]+$');
        var reg = new RegExp('(^([0-9]*[.][0-9]*[0-9]+[0-9]*)$)|(^([0-9]*[0-9]+[0-9]*[.][0-9]+)$)|(^([0-9]+[0-9]*)$)');

        if (control.value) {
            if (reg.test(control.value) && regInteger.test(control.value)) {
                if (+control.value <= 0) {
                    return { 'positiveNumeric': true };
                } else if (+control.value > 2147483647) {
                    return { 'numberToLarge': true };
                } else {
                    return null;
                }

            }

            return { 'positiveNumeric': true };
        }
    }

    static integer(control) {
        var reg = new RegExp('^-?[0-9]+$');
        if (control.value) {
            if (reg.test(control.value)) {
                return null;
            } else {
                return { 'requiredInteger': true };
            }
        }

        return null;
    }

    static requiredInteger(control) {
        var reg = new RegExp('^-?[0-9]+$');
        if (reg.test(control.value)) {
            return null;
        }

        return { 'requiredInteger': true };
    }

    static requireSelectedItem(control) {
        if (control.value != 0) {
            return null;
        }

        return { 'requireSelectedItem': true };
    }

    static creditCardValidator(control) {
        // Visa, MasterCard, American Express, Diners Club, Discover, JCB
        if (control.value.match(/^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/)) {
            return null;
        } else {
            return { 'invalidCreditCard': true };
        }
    }

    static emailValidator(control) {
        // RFC 2822 compliant regex
        if (control.value && control.value.match(/[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/)) {
            return null;
        } else {
            return { 'invalidEmailAddress': true };
        }
    }

    static invalidUserName(control) {
        if (control.value && control.value.match(/^[a-zA-Z][a-zA-Z0-9\.]*$/)) {
            return null;
        } else {
            return { 'invalidUserName': true };
        }
    }

    static nameValidator(control) {
        if (control.value && control.value.match(/^[a-zA-Z][a-zA-Z0-9\s]*$/)) {
            return null;
        } else {
            return { 'invalidName': true };
        }
    }

    static mcAuditNameValidator(control) {
        if (control.value && control.value.match(/^[a-zA-Z0-9!@#$&()\-\\\_:;'.+,\/\"\s]*$/)) {
            return null;
        } else {
            return { 'invalidName': true };
        }
    }

    static optionalEmailValidator(control) {
        // RFC 2822 compliant regex
        if (!control.value) {
            return null;
        }
        if (control.value && control.value.match(/[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/)) {
            return null;
        } else {
            return { 'invalidEmailAddress': true };
        }
    }

    static passwordValidator(control) {
        // {6,100}           - Assert password is between 6 and 100 characters
        // (?=.*[0-9])       - Assert a string has at least one number
        if (control.value.match(/^(?=.*[0-9])[a-zA-Z0-9!@#$%^&*]{6,100}$/)) {
            return null;
        } else {
            return { 'invalidPassword': true };
        }
    }

    static phoneValidator(control) {
        if (control.value && (control.value.match(/^((\+)?[1-9]{1,2})?([-\s\.])?((\(\d{1,4}\))|\d{1,4})(([-\s\.])?[0-9]{1,12}){1,2}$/)
            || control.value.match(/^011 ([1-9][0-9]{1,5}) ([0-9]+) ([0]?\d[0-9]+)(?:[^\d\ ]+(\d{1,4}))?$/))) {
            return null;
        } else {
            return { 'invalidPhone': true };
        }
    }

    static optionalPhoneValidator(control) {
        if (!control.value) {
            return null;
        }
        if (control.value && (control.value.match(/^((\+)?[1-9]{1,2})?([-\s\.])?((\(\d{1,4}\))|\d{1,4})(([-\s\.])?[0-9]{1,12}){1,2}$/)
            || control.value.match(/^011 ([1-9][0-9]{1,5}) ([0-9]+) ([0]?\d[0-9]+)(?:[^\d\ ]+(\d{1,4}))?$/))) {
            return null;
        } else {
            return { 'invalidPhone': true };
        }
    }

    static optionalPhoneWithExtensionValidator(control) {
        if (!control.value) {
            return null;
        }

        var rawValue = control.value;
        var cleanValue = rawValue.replaceAll('_', '').trim();

        if (cleanValue &&
            cleanValue.match(/^(\+)?(\()?(\d{3})?(\))?([\-\s\.])?(\d{3})+([\-\s\.])?(\d{4})+([\-\s\.\+])?([\d^_]{1,5})?$/)) {
            return null;
        } else {
            return { 'invalidPhone': true };
        }
    }

    static requiredModelYear(control) {
        if ((control.value >= 1800) && (control.value <= new Date().getFullYear() + 2)) {
            return null;

        }

        return { 'requiredModelYear': true };
    }

    static maxAircraftModelYear(control) {
        if (control.value <= new Date().getFullYear() + 1) {
            return null;

        }

        return { 'maxAircraftModelYear': true };
    }

    static yearFirstRegisteredRange(control: any) {
        if ((control.value >= 1800) && (control.value <= new Date().getFullYear())) {
            return null;
        }

        return { 'yearFirstRegisteredRange': true };
    }

    static yearFirstRegisteredRange2(control: any) {
        if ((control.value >= 1800) && (control.value <= (new Date().getFullYear() + 1))) {
            return null;
        }

        return { 'yearFirstRegisteredRange2': true };
    }

    static requiredVINPattern(control) {
        let isValid = false;

        // make sure the control has a value before we make the call
        if (control.value != undefined && control.value.length >= 1) {

            // get an HTTP object
            var injector = Injector.create({ providers: [{ provide: BaseWrapperService, deps: [] }] });
            const httpObj = injector.get(BaseWrapperService);

            if (httpObj == null) {
                throw new ReferenceError('Http object not available in requiredVINPattern');
            }

            // validate using the service
            httpObj.simpleGet('Vehicle/Vehicle/VIN/Validate/' + control.value)
                .subscribe(result => isValid = result,
                    e => console.log('error', e),
                    () => {
                        if (isValid) {
                            return null;
                        }

                        return { 'requiredVINPattern': true };
                    });
        } else {
            return { 'requiredVINPattern': true };
        }
    }

    static requiredVinPatternCompareToOriginalVin(control) {
        let originalVinControl = control.parent.get('originalVin');
        if (!originalVinControl) {
            return ControlValidationMessagesService.requiredVINPattern(control);
        } else {
            let originalVin = control.parent.get('originalVin').value;
            if (originalVin != control.value) {
                control.parent.get('originalVin').setValue(control.value);
                return ControlValidationMessagesService.requiredVINPattern(control);
            }
        }
        return null;
    }

    /**
     * Validates that the input string is a valid date formatted as "mm/dd/yyyy".  For binding purposes, dates formatted as the ISO8601 (YYYY-MM-DDT00:00:00-00:00) will also pass.
     * @param date - date string
     */
    static shortDateValidator(control) {
        if (control.value && control.value !== null) {
            let trimmedValue = '';
            try {
                if (control.value.value) {
                    trimmedValue = control.value.value.trim();
                } else {
                    trimmedValue = control.value.trim();
                }
            } catch (e) {
                return null;
            }

            var month = null;
            var year = null;
            var day = null;

            if (/(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})[+-](\d{2})\:(\d{2})/.test(trimmedValue)) {
                var parts = trimmedValue.split('-');
                month = parseInt(parts[1], 10);
                year = parseInt(parts[0], 10);
                var dayParts = parts[2].split('T');
                day = parseInt(dayParts[0], 10);
            } else {
                // First check for the pattern
                if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmedValue)) {
                    return { 'invalidShortDate': true };
                }

                // Parse the date parts to integers
                var parts = trimmedValue.split('/');
                day = parseInt(parts[1], 10);
                month = parseInt(parts[0], 10);
                year = parseInt(parts[2], 10);
            }

            // Check the ranges of month and year
            if (year < 1000 || year > 3000 || month == 0 || month > 12) {
                return { 'invalidShortDate': true };
            }

            var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

            // Adjust for leap years
            if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0)) {
                monthLength[1] = 29;
            }

            // Check the range of the day
            if (day > 0 && day <= monthLength[month - 1]) {
                return null;
            } else {
                return { 'invalidShortDate': true };
            }
        } else {
            return null;
        }
    }

    /**
 * Requires the ShortDate is validated.  The input string must be a valid date formatted as "mm/dd/yyyy".  For binding purposes, dates formatted as the ISO8601 (YYYY-MM-DDT00:00:00-00:00) will also pass.
 * @param date - date string
 */
    static requiredShortDateValidator(control) {
        if (control.value && control.value !== null) {
            return ControlValidationMessagesService.shortDateValidator(control);
        } else {
            return { 'invalidShortDate': true };
        }
    }

    /**
     * Validates that the input string is a valid date formatted as "mm/dd/yyyy".  For binding purposes, dates formatted as the ISO8601 (YYYY-MM-DDT00:00:00-00:00) will also pass.
     * @param date - date string
     */
    static minDateValidator(control) {
        if (control.value && control.value !== null) {
            var m = moment(control.value);
            if (m.isAfter(EARLY_DATE)) {
                return null;
            } else {
                return { 'invalidMinimumDate': true };
            }

        } else {
            return null;
        }
    }

    static minDateRequired(control) {
        if (control.value && control.value !== null) {
            return null;

        } else {
            return { 'minDateRequired': true };
        }
    }

    static dateValidator(control) {
        if (control.value) {
            var trimmedValue = control.value.trim();
            var reg = new RegExp('^([0]?[1-9]|[1][0-2])[/]([0]?[1-9]|[1|2][0-9]|[3][0|1])[/]([0-9]{4})$');
            if (reg.test(trimmedValue)) {
                var year = trimmedValue.substring(control.value.lastIndexOf('/') + 1);

                if (year === '00'
                    || year === '000'
                    || year === '0000') {
                    return { 'validDateRequired': true };
                }

                return null;
            }
            return { 'validDateRequired': true };
        }
    }

    static permanentResidentReceiptNumber(control) {
        if (control.value) {
            if (/^[A-Z]{3}[A-Z0-9]{10}$/.test(control.value)) {
                return null;
            }
            return { 'permanentResidentReceiptNumber': true };
        }
    }

    static documentCertificateNumber(control) {
        if (control.value) {
            if (/^[A-Z]{3}[A-Z0-9]{10}$/.test(control.value)) {
                return null;
            }
            return { 'documentCertificateNumber': true };
        }
    }

    static employmenteAuthorizationCardNumber(control) {
        if (control.value) {
            if (/^[A-Z]{3}[A-Z0-9]{10}$/.test(control.value)) {
                return null;
            }
            return { 'employmenteAuthorizationCardNumber': true };
        }
    }

    static naturalizationCertificateNumber(control) {
        if (control.value) {
            if (/^[A-Z0-9]{6,12}$/.test(control.value)) {
                return null;
            }
            return { 'naturalizationCertificateNumber': true };
        }
    }

    static documentDescriptionLength(control) {
        if (control.value != null && (control.value.length >= 0 && control.value.length <= 35)) {
            return null;
        }

        return { 'documentDescriptionLength': true };
    }

    static citizenshipCertificateNumber(control) {
        if (control.value) {
            if (/^[A-Z0-9]{6,12}$/.test(control.value)) {
                return null;
            }
            return { 'citizenshipCertificateNumber': true };
        }
    }

    static monthRangeValidator(control) {
        if (control.value) {
            if (control.value >= 1 && control.value <= 12) {
                return null;
            }

            return { 'invalidMonth': true };
        }

        return { 'invalidMonth': true };
    }

    static minimumYearValidator(control) {
        if (control.value) {
            if (control.value > 1752) {
                return null;
            }

            return { 'invalidMinimumYear': true };
        }

        return { 'invalidMinimumYear': true };
    }

    static allowPositiveNumberAndDecimals(control) {
        if (control.value) {
            if (/^[+]?([0-9]+(?:[\.][0-9]*)?|\.[0-9]+)$/.test(control.value)) {
                return null;
            }
            return { 'allowPositiveNumberAndDecimals': true };
        }
    }

    static minAndMaxNumber(control) {
        if (control.value && control.value && control.value < 0.0001 && control.value > 10.0000) {
            return { 'minAndMaxNumber': true };
        }

        return null;
    }

    static invalidFutureDate(control) {
        if (control.value && (new Date(control.value).getTime() > new Date().getTime())) {
            return { 'dateCannotBeInFuture': true };
        }
    }

    static highCategoryLimit(control) {
        if (control.value && control.value && control.value < 25.00) {
            return { 'highCategoryLimit': true };
        }

        return null;
    }

    static lowCategoryLimit(control) {
        if (control.value && control.value && control.value < 15.00) {
            return { 'lowCategoryLimit': true };
        }

        return null;
    }

    static invalidFactoryListPrice(control) {
        if (control.value && control.value && (control.value > 999999.99 || control.value < .01)) {
            return { 'invalidFactoryListPrice': true };
        }
    }

    static fourDigitYearRequired(control) {
        if (!/^\d{4}$/.test(control.value)) {
            return { 'fourDigitYearRequired': true };
        }
    }
}
